import { useState, useEffect, useRef } from 'react'
import { useTenantConfig } from '@webmono/storefront-ui/providers'
import type { Order, OrderStatus } from '@webmono/storefront-ui/types'
import { formatCents, cn } from '@webmono/storefront-ui/utils'
import { useMerchantOrders, useUpdateOrderStatus } from '@/lib/hooks'

const STATUS_TABS: { status: OrderStatus | 'all'; label: string }[] = [
  { status: 'all', label: 'All' },
  { status: 'pending', label: 'New' },
  { status: 'confirmed', label: 'Confirmed' },
  { status: 'preparing', label: 'Preparing' },
  { status: 'ready', label: 'Ready' },
]

function getElapsedColor(minutes: number): string {
  if (minutes < 10) return 'text-success'
  if (minutes < 20) return 'text-warning'
  return 'text-destructive'
}

function OrderCard({ order, onStatusUpdate }: { order: Order; onStatusUpdate: (status: OrderStatus) => void }) {
  const tenant = useTenantConfig()
  const placedAt = new Date(order.timestamps.placedAt)
  const elapsedMinutes = Math.floor((Date.now() - placedAt.getTime()) / 60000)

  const getNextStatus = (): OrderStatus | null => {
    switch (order.status) {
      case 'pending':
        return 'confirmed'
      case 'confirmed':
        return 'preparing'
      case 'preparing':
        return 'ready'
      case 'ready':
        return order.fulfillmentType === 'delivery' ? 'out_for_delivery' : 'picked_up'
      default:
        return null
    }
  }

  const nextStatus = getNextStatus()

  const getActionLabel = (): string => {
    switch (order.status) {
      case 'pending':
        return 'Accept Order'
      case 'confirmed':
        return 'Start Preparing'
      case 'preparing':
        return 'Mark Ready'
      case 'ready':
        return order.fulfillmentType === 'delivery' ? 'Out for Delivery' : 'Mark Picked Up'
      default:
        return ''
    }
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-semibold text-foreground">#{order.orderNumber}</span>
              <span className={cn(
                'text-xs px-2 py-0.5 rounded-full font-medium capitalize',
                order.status === 'pending' && 'bg-warning/10 text-warning',
                order.status === 'confirmed' && 'bg-primary/10 text-primary',
                order.status === 'preparing' && 'bg-primary/10 text-primary',
                order.status === 'ready' && 'bg-success/10 text-success',
                order.status === 'picked_up' && 'bg-muted text-muted-foreground',
                order.status === 'delivered' && 'bg-muted text-muted-foreground',
              )}>
                {order.status.replace('_', ' ')}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{order.customer.name}</p>
          </div>
          <div className="text-right">
            <span className={cn('font-mono text-sm', getElapsedColor(elapsedMinutes))}>
              {elapsedMinutes}m ago
            </span>
            <p className="text-xs text-muted-foreground capitalize">{order.fulfillmentType.replace('_', ' ')}</p>
          </div>
        </div>

        <div className="space-y-1 mb-3">
          {order.items.map((item) => (
            <div key={item.id} className="flex justify-between text-sm">
              <span className="text-foreground">
                <span className="font-medium">{item.quantity}x</span> {item.menuItem.name}
                {item.selectedModifiers.length > 0 && (
                  <span className="text-muted-foreground text-xs ml-1">
                    ({item.selectedModifiers.map((m) => m.modifier.name).join(', ')})
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>

        {order.specialInstructions && (
          <div className="text-sm text-muted-foreground italic bg-muted/50 p-2 rounded mb-3">
            "{order.specialInstructions}"
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <span className="font-mono font-semibold text-foreground">
            {formatCents(order.totals.totalCents, tenant.operations.currency)}
          </span>

          {nextStatus && (
            <button
              onClick={() => onStatusUpdate(nextStatus)}
              className={cn(
                'px-4 py-2 rounded-lg font-medium text-sm transition-colors',
                order.status === 'pending'
                  ? 'bg-success text-success-foreground hover:bg-success/90'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90'
              )}
            >
              {getActionLabel()}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function MerchantOrdersPage() {
  const [activeTab, setActiveTab] = useState<OrderStatus | 'all'>('all')
  const { data: orders, isLoading, refetch } = useMerchantOrders()
  const updateStatus = useUpdateOrderStatus()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [lastOrderCount, setLastOrderCount] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      refetch()
    }, 5000)
    return () => clearInterval(interval)
  }, [refetch])

  useEffect(() => {
    if (orders && orders.length > lastOrderCount && lastOrderCount > 0) {
      if (audioRef.current) {
        audioRef.current.play().catch(() => {})
      }
    }
    if (orders) {
      setLastOrderCount(orders.length)
    }
  }, [orders, lastOrderCount])

  const handleStatusUpdate = async (orderId: string, status: OrderStatus) => {
    await updateStatus.mutateAsync({ orderId, status })
    refetch()
  }

  const filteredOrders = orders?.filter(
    (order) => activeTab === 'all' || order.status === activeTab
  ) ?? []

  const pendingCount = orders?.filter((o) => o.status === 'pending').length ?? 0

  return (
    <div>
      <audio ref={audioRef} src="/notification.mp3" preload="auto" />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold text-foreground">Orders</h1>
        {pendingCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-warning/10 text-warning animate-pulse">
            <span className="w-2 h-2 bg-warning rounded-full" />
            <span className="font-medium text-sm">{pendingCount} new order{pendingCount > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide pb-2">
        {STATUS_TABS.map((tab) => {
          const count = tab.status === 'all'
            ? orders?.length ?? 0
            : orders?.filter((o) => o.status === tab.status).length ?? 0

          return (
            <button
              key={tab.status}
              onClick={() => setActiveTab(tab.status)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap',
                activeTab === tab.status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              )}
            >
              {tab.label}
              <span className={cn(
                'text-xs px-1.5 py-0.5 rounded-full',
                activeTab === tab.status
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              )}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-4 animate-pulse">
              <div className="h-5 bg-muted rounded w-1/3 mb-2" />
              <div className="h-4 bg-muted rounded w-1/2 mb-4" />
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h2 className="text-lg font-medium text-foreground mb-1">No orders</h2>
          <p className="text-muted-foreground">Orders will appear here when customers place them</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onStatusUpdate={(status) => handleStatusUpdate(order.id, status)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
